import React, { useCallback, useMemo } from 'react';

import { DiagnosticContributorBoundary } from 'features/diagnostics/components/diagnostic-contributor-boundary';
import { diagnosticMoney } from 'features/diagnostics/context/projectors';
import {
  useDiagnosticContributor,
  useDiagnosticToolsEnabled,
} from 'features/diagnostics/context/provider';
import { DiagnosticPriceContribution } from 'features/diagnostics/context/types';

import { useProductPrice } from 'hooks/menu/use-product-price';
import { useLocale } from 'state/intl';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { useUIContext } from 'state/ui';

const readPlu = (entry: unknown): string | undefined => {
  const source = entry as Record<string, unknown> | null;
  if (!source) {
    return undefined;
  }
  for (const key of ['plu', 'constantPlu', 'externalReferencesId']) {
    if (typeof source[key] === 'string') {
      return source[key] as string;
    }
  }
  return undefined;
};

const legacyName = (entry: unknown): string | undefined => {
  const source = entry as { name?: string | { locale?: string } } | null;
  return typeof source?.name === 'string' ? source.name : source?.name?.locale;
};

const legacyPickerSelectionName = (
  menuObject: unknown,
  aspectId: string,
  selectedIdentifier: string
): string => {
  const source = menuObject as {
    pickerAspects?: Array<{
      _id?: string;
      pickerAspectOptions?: Array<{ identifier?: string; name?: { locale?: string } }>;
    }>;
  };
  return (
    source.pickerAspects
      ?.find(aspect => aspect._id === aspectId)
      ?.pickerAspectOptions?.find(option => option.identifier === selectedIdentifier)?.name
      ?.locale ?? selectedIdentifier
  );
};

export const LegacyDiagnosticPdpContributor = () => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? (
    <DiagnosticContributorBoundary>
      <EnabledLegacyDiagnosticPdpContributor />
    </DiagnosticContributorBoundary>
  ) : null;
};

const EnabledLegacyDiagnosticPdpContributor = () => {
  const {
    menuObject,
    productQuantity,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();
  const flow = useProductWizardFlow();
  const basePrice = useProductPrice({
    shouldIncludeModifications: false,
    shouldIncludeQuantity: false,
  });
  const configuredPrice = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: false,
  });
  const displayedPrice = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: true,
  });
  const { formatCurrencyForLocale } = useUIContext();
  const { priceForItemInComboSlotSelection, priceForItemOptionModifier } = useMenuContext();
  const { region } = useLocale();
  const currency = String(region).toUpperCase() === 'CA' ? 'CAD' : 'USD';
  const displayedAmount = displayedPrice.displayedProductPrice;

  const projectSelections = useCallback((): DiagnosticPriceContribution[] => {
    const projected: DiagnosticPriceContribution[] = Object.entries(
      userSelections.pickerAspects ?? {}
    ).map(([id, selectedId]) => ({
      kind: 'picker',
      id,
      sanityId: selectedId,
      name: legacyPickerSelectionName(menuObject, id, selectedId),
    }));
    userSelections.comboSlot?.forEach((selection, slotId) => {
      const contribution = selection.selectedItem
        ? priceForItemInComboSlotSelection({
            combo: selectedProduct as Parameters<
              typeof priceForItemInComboSlotSelection
            >[0]['combo'],
            comboSlot: selection.comboSlot ?? undefined,
            selectedItem: selection.selectedItem,
          })
        : undefined;
      projected.push({
        kind: 'slot',
        id: slotId,
        sanityId: selection.selectedItem?._id,
        name: legacyName(selection.selectedItem),
        plu: readPlu(selection.selectedItem?.vendorConfigs),
        quantity: 1,
        price:
          contribution === undefined
            ? undefined
            : diagnosticMoney({
                minorAmount: contribution,
                currency,
                formatted: formatCurrencyForLocale(contribution),
                source: 'menu_calculation',
                availability: 'available',
              }),
      });
    });
    for (const [slotId, modifiers] of Object.entries(userSelections.modifiers ?? {})) {
      for (const [modifierId, choices] of Object.entries(modifiers ?? {})) {
        for (const [choiceId, choice] of Object.entries(choices ?? {})) {
          const selection = choice as {
            item?: Parameters<typeof priceForItemOptionModifier>[0]['item'];
            option?: Parameters<typeof priceForItemOptionModifier>[0]['itemOption'];
            modifier?: Parameters<typeof priceForItemOptionModifier>[0]['modifier'] & {
              _key?: string;
              name?: { locale?: string };
              modifierMultiplier?: { multiplier?: number };
            };
          };
          const multiplier = selection.modifier?.modifierMultiplier?.multiplier;
          if (!multiplier) {
            continue;
          }
          const contribution = priceForItemOptionModifier({
            item: selection.item ?? null,
            itemOption: selection.option ?? null,
            modifier: selection.modifier ?? null,
          });
          projected.push({
            kind: 'modifier',
            id: modifierId,
            sanityId: selection.modifier?._key ?? choiceId,
            name:
              legacyName(selection.option) ??
              legacyName(selection.modifier) ??
              `${slotId}:${modifierId}`,
            multiplier,
            price: diagnosticMoney({
              minorAmount: contribution,
              currency,
              formatted: formatCurrencyForLocale(contribution),
              source: 'menu_calculation',
              availability: 'available',
            }),
          });
        }
      }
    }
    return projected;
  }, [
    currency,
    formatCurrencyForLocale,
    menuObject,
    priceForItemInComboSlotSelection,
    priceForItemOptionModifier,
    selectedProduct,
    userSelections,
  ]);

  const getter = useMemo(
    () => () => {
      const baseAmount = basePrice.totalPrice();
      const configuredAmount = configuredPrice.totalPrice();
      return {
        pdp: {
          implementation: 'legacy' as const,
          menuObjectType: String((menuObject as { _type?: string })._type ?? ''),
          rootId: menuObject._id,
          selectedId: selectedProduct._id,
          sanityId: selectedProduct._id,
          name: legacyName(selectedProduct),
          plu: readPlu(selectedProduct.vendorConfigs),
          quantity: productQuantity,
          step: String(flow.currentStep),
          mode: flow.cartIdEditing ? ('edit' as const) : ('add' as const),
          cartEntryId: flow.cartIdEditing || undefined,
          rewardId: flow.rewardId,
          rewardBenefitId: flow.rewardBenefitId,
          baseUnitPrice: diagnosticMoney({
            minorAmount: baseAmount,
            currency,
            formatted: formatCurrencyForLocale(baseAmount),
            source: 'menu_calculation',
            availability: 'available',
          }),
          configuredUnitPrice: diagnosticMoney({
            minorAmount: configuredAmount,
            currency,
            formatted: formatCurrencyForLocale(configuredAmount),
            source: 'menu_calculation',
            availability: 'available',
          }),
          displayedTotal: diagnosticMoney({
            minorAmount: displayedAmount,
            currency,
            formatted: displayedPrice.formattedProductPrice,
            source: 'displayed_ui',
            availability: displayedPrice.formattedProductPrice ? 'available' : 'unavailable',
          }),
          selections: projectSelections(),
        },
      };
    },
    [
      basePrice,
      configuredPrice,
      currency,
      displayedAmount,
      displayedPrice.formattedProductPrice,
      flow.cartIdEditing,
      flow.currentStep,
      flow.rewardBenefitId,
      flow.rewardId,
      formatCurrencyForLocale,
      menuObject,
      productQuantity,
      selectedProduct,
      projectSelections,
    ]
  );
  useDiagnosticContributor('pdp-legacy', getter);

  return null;
};
